import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { FormState } from 'react-hook-form';
import { Button } from 'reactstrap';
import { BackArrow } from '../../../../components/BackArrow';
import { Card } from '../../../../components/Card';
import { LoadingSpinner } from '../../../../components/LoadingSpinner';
import { PageTitle } from '../../../../components/PageTitle';
import { JobPost } from '../../../../entities/applicant_tracking/JobPost';
import { Source } from '../../../../entities/applicant_tracking/Source';
import { CandidateDto } from '../../../../services/applicant_tracking/CandidateService/CandidateDto';
import { JobPostService } from '../../../../services/applicant_tracking/JobPostService';
import { ApplicationForm } from '../ApplicationForm';
import { CandidatePageHeaderStatusInfo } from '../../../CandidatePage/CandidatePageHeader/CandidatePageHeaderStatusInfo';
import { CandidatePageHeaderJobInfo } from '../../../CandidatePage/CandidatePageHeader/CandidatePageHeaderJobInfo';
import { mergeCandidateApplications } from '../../Candidates/utils/mergeCandidateApplications';
import { SubmitButton } from '../../../../components/SubmitButton';
import { SourceService } from '../../../../services/v1/applicant_tracking/SourceService';
import { getRedirectUriQueryParam } from '../../../../utils/url';
import { QueryClientProvider } from 'react-query';
import { queryClient } from '../../../../config/queryClient';
import { ApplicationService } from '../../../../services/v1/ApplicationService';
import { Application } from '../../../../entities/v1/applicant_tracking/Application';

interface PropTypes {
  applicationId: number;
  applicationDocumentsFf: boolean;
}

export default function EditApplicationPage(props: PropTypes) {
  const [application, setApplication] = useState<Application>(null);
  const [jobPosts, setJobPosts] = useState<JobPost[]>(null);
  const [appliedJobPostIds, setAppliedJobPostIds] = useState<number[]>([]);
  const [sources, setSources] = useState<Source[]>(null);

  const [candidateDto, setCandidateDto] = useState<CandidateDto>(null);

  useEffect(() => {
    const fetchData = async () => {
      const applicationPromise = ApplicationService.show(props.applicationId, [
        'candidate',
        'job_post',
        'application_resume',
        'application_cover_letter',
        'documents',
      ]);
      const jobPostsPromise = JobPostService.list({ restrictedAccess: true });
      const sourcesPromise = SourceService.list({
        pageSize: 100,
        excludeSourceReferrer: true,
      });

      const [application, jobPosts, sourceList] = await Promise.all([
        applicationPromise,
        jobPostsPromise,
        sourcesPromise,
      ]);

      setApplication(application);
      setJobPosts(jobPosts.jobPosts);
      setAppliedJobPostIds(jobPosts.disabledJobPostIds);
      setSources(sourceList.sources);

      setCandidateDto(mergeCandidateApplications(null, application));
    };

    fetchData();
  }, []);

  async function handleSuccessfulSubmit(applicationId: number) {
    window.location.href =
      getRedirectUriQueryParam() ?? `/applications/${applicationId}`;
  }

  if (application == null) return <LoadingSpinner />;

  return (
    <QueryClientProvider client={queryClient}>
      <div className='d-flex flex-column'>
        <PageHeader application={application} />
        <Card className='mt-4'>
          <ApplicationForm
            applicationDocumentsFf={props.applicationDocumentsFf}
            candidateId={application.candidateId}
            applicationId={application.id}
            jobPosts={jobPosts}
            appliedJobPostIds={appliedJobPostIds}
            sources={sources}
            onSuccessfulSubmit={handleSuccessfulSubmit}
            headerFactory={(formState) => (
              <FormHeader formState={formState} application={application} />
            )}
            candidateDto={candidateDto}
          />
        </Card>
      </div>
    </QueryClientProvider>
  );
}

function PageHeader(props: { application: Application }) {
  return (
    <div className='d-flex align-items-center'>
      <div className='me-auto d-flex align-items-center'>
        <BackArrow />
        <PageTitle text={props.application.candidate.name} />
        <CandidatePageHeaderStatusInfo
          status={props.application.status}
          classNames={'ms-2h'}
        />
        <CandidatePageHeaderJobInfo
          jobId={props.application.jobPost.jobId}
          jobName={props.application.jobPost.name}
        />
      </div>
      <div className='fs-5 text-muted'>
        Last Updated at{' '}
        {moment(props.application.lastInteractionAt).format(
          'MMMM DD, YYYY, hh:mm A',
        )}
      </div>
    </div>
  );
}

function FormHeader(props: {
  formState: FormState<CandidateDto>;
  application: Application;
}) {
  return (
    <div className='d-flex'>
      <h2>Edit Job Application</h2>
      <div className='ms-auto d-flex gap-2'>
        <Button
          color='danger'
          onClick={() =>
            (window.location.href =
              getRedirectUriQueryParam() ??
              `/applications/${props.application.id}`)
          }
        >
          Cancel
        </Button>
        <SubmitButton formState={props.formState}>Save and Close</SubmitButton>
      </div>
    </div>
  );
}
